import React, { useState, useEffect } from 'react';
import './History.css';

interface HistoricalData {
  timestamp: string;
  seedpool: TrackerData;
}

interface TrackerData {
  totalTorrents: number;
  totalTorrentsSize: string | number;
  seedSize: string | number;
  totalUsers: number;
  totalPeers: number;
  activeThisWeek?: number;
}

const MAX_DISPLAY_POINTS = 100;

const parseSizeForValidation = (sizeStr: string | number | undefined): number => {
  if (typeof sizeStr === 'number') return sizeStr;
  if (!sizeStr || sizeStr === '0 B') return 0;

  const match = String(sizeStr).trim().match(/(\d+\.?\d*)\s*(PiB|TiB|GiB|MiB|KiB|B)/i);
  if (!match) return 0;

  const value = parseFloat(match[1]);
  const unit = match[2].toUpperCase();
  const multipliers: { [key: string]: number } = {
    B: 1 / (1024 ** 4),
    KIB: 1024 / (1024 ** 4),
    MIB: (1024 ** 2) / (1024 ** 4),
    GIB: (1024 ** 3) / (1024 ** 4),
    TIB: 1,
    PIB: 1024
  };

  const result = value * (multipliers[unit] || 0);
  return Number.isNaN(result) ? 0 : result;
};

const isValidHistoryEntry = (entry: HistoricalData): boolean => {
  const stats = entry.seedpool;
  if (!stats) return false;

  if ((stats.totalTorrents || 0) === 0) return false;
  if ((stats.totalUsers || 0) === 0) return false;
  if ((stats.totalPeers || 0) === 0) return false;
  if ((stats.activeThisWeek || 0) === 0) return false;
  if (parseSizeForValidation(stats.seedSize) === 0) return false;
  if (parseSizeForValidation(stats.totalTorrentsSize) === 0) return false;

  return true;
};

const isStaleSnapshotEntry = (entry: HistoricalData): boolean => {
  const stats = entry.seedpool;
  if (!stats) return false;

  return stats.totalTorrents === 435493
    && stats.totalUsers === 24431
    && stats.totalPeers === 1204347
    && String(stats.seedSize).includes('13.53');
};

const filterValidHistoryEntries = (history: HistoricalData[]): HistoricalData[] => {
  return history.filter((entry) => isValidHistoryEntry(entry) && !isStaleSnapshotEntry(entry));
};

const History: React.FC = () => {
  const [historicalData, setHistoricalData] = useState<HistoricalData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{ visible: boolean; x: number; y: number; content: string }>({
    visible: false,
    x: 0,
    y: 0,
    content: ''
  });

  useEffect(() => {
    fetchHistoricalData();
  }, []);

  const fetchHistoricalData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/history?t=' + Date.now());
      if (!response.ok) {
        throw new Error('Failed to fetch historical data');
      }
      const data = await response.json();
      
      const sortedData = filterValidHistoryEntries(data)
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      
      const displayData = limitDataPoints(sortedData, MAX_DISPLAY_POINTS);
      setHistoricalData(displayData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch historical data');
    } finally {
      setLoading(false);
    }
  };

  // Downsample for rendering while preserving the full timeline span.
  // Never reuse the same source point — gaps (e.g. outage) otherwise paint
  // the latest value as several identical stacked dots.
  const limitDataPoints = (data: HistoricalData[], maxPoints: number): HistoricalData[] => {
    if (data.length <= maxPoints) {
      return data;
    }

    const sortedData = [...data].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    const earliestTime = new Date(sortedData[0].timestamp).getTime();
    const latestTime = new Date(sortedData[sortedData.length - 1].timestamp).getTime();
    const timeStep = (latestTime - earliestTime) / (maxPoints - 1);
    const usedIndexes = new Set<number>();
    const limitedData: HistoricalData[] = [];

    for (let i = 0; i < maxPoints; i++) {
      const targetTime = earliestTime + i * timeStep;

      let closestIndex = -1;
      let closestDistance = Number.POSITIVE_INFINITY;

      for (let j = 0; j < sortedData.length; j++) {
        if (usedIndexes.has(j)) continue;
        const distance = Math.abs(new Date(sortedData[j].timestamp).getTime() - targetTime);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = j;
        }
      }

      if (closestIndex === -1) break;
      usedIndexes.add(closestIndex);
      limitedData.push(sortedData[closestIndex]);
    }

    return limitedData.sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  };

  // Helper function to parse size strings and convert to TiB for comparison
  const parseSize = (sizeStr: string | number): number => {
    if (typeof sizeStr === 'number') return sizeStr;
    if (!sizeStr || sizeStr === 'undefined' || sizeStr === 'null') return 0;
    
    const str = sizeStr.toString().trim();
    const match = str.match(/(\d+\.?\d*)\s*(PiB|TiB|GiB|MiB|KiB|B)/i);
    if (!match) return 0;
    
    const value = parseFloat(match[1]);
    const unit = match[2].toUpperCase();
    
    // Convert everything to TiB for consistent comparison
    const multipliers: { [key: string]: number } = {
      'B': 1 / (1024 * 1024 * 1024 * 1024),
      'KIB': 1024 / (1024 * 1024 * 1024 * 1024),
      'MIB': (1024 * 1024) / (1024 * 1024 * 1024 * 1024),
      'GIB': (1024 * 1024 * 1024) / (1024 * 1024 * 1024 * 1024),
      'TIB': 1,
      'PIB': 1024
    };
    
    const result = value * (multipliers[unit] || 0);
    return isNaN(result) ? 0 : result;
  };

  const formatValue = (value: number, type: 'torrents' | 'size' | 'users' | 'peers'): string => {
    if (isNaN(value)) {
      return '0';
    }
    if (type === 'size') {
      // For size, show exact values with proper units
      if (value >= 1024) {
        return `${(value / 1024).toFixed(2)} PiB`;
      }
      return `${value.toFixed(2)} TiB`;
    }
    // For all other types, show exact numbers with commas for readability
    return Math.round(value).toLocaleString();
  };

  const getMaxValue = (data: HistoricalData[], key: keyof TrackerData): number => {
    return Math.max(
      ...data.map(d => 
        (key === 'seedSize' || key === 'totalTorrentsSize') ? parseSize(d.seedpool[key] as string) : d.seedpool[key] as number
      )
    );
  };

  // Helper function to find peaks and valleys in data
  const findPeaksAndValleys = (data: number[]): { peaks: number[], valleys: number[] } => {
    const peaks: number[] = [];
    const valleys: number[] = [];
    
    if (data.length === 0) return { peaks, valleys };
    
    // Find the maximum value
    const maxValue = Math.max(...data);
    
    // Peak is simply the highest point(s) - find all indices with the max value
    data.forEach((value, index) => {
      if (value === maxValue) {
        peaks.push(index);
      }
    });
    
    // Find valleys (local minima)
    for (let i = 1; i < data.length - 1; i++) {
      const prev = data[i - 1];
      const current = data[i];
      const next = data[i + 1];
      
      // Valley: current is lower than both neighbors
      if (current < prev && current < next) {
        valleys.push(i);
      }
    }
    
    return { peaks, valleys };
  };

  // Helper function to calculate trend direction
  const calculateTrend = (data: number[]): 'rising' | 'falling' | 'stable' => {
    if (data.length < 2) return 'stable';
    
    const first = data[0];
    const last = data[data.length - 1];
    const change = (last - first) / first;
    
    if (change > 0.05) return 'rising';
    if (change < -0.05) return 'falling';
    return 'stable';
  };

  const renderLineGraph = (title: string, key: keyof TrackerData, type: 'torrents' | 'size' | 'users' | 'peers') => {
    if (historicalData.length === 0) {
      return (
        <div className="graph-section">
          <h3 className="graph-title">{title}</h3>
          <div className="no-data">No historical data available yet</div>
        </div>
      );
    }

    const maxValue = getMaxValue(historicalData, key);
    if (maxValue === 0) {
      return (
        <div className="graph-section">
          <h3 className="graph-title">{title}</h3>
          <div className="no-data">No data available for this metric</div>
        </div>
      );
    }

    const width = Math.min(window.innerWidth - 80, 1400);
    const height = 600;
    const padding = 80;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    const trackers = [
      { name: 'Seedpool', color: '#7dd3fc', data: historicalData.map(d => {
        const rawValue = d.seedpool[key];
        if (rawValue === undefined || rawValue === null) return 0;
        const value = key === 'seedSize' || key === 'totalTorrentsSize' ? parseSize(rawValue) : Number(rawValue);
        return isNaN(value) ? 0 : value;
      }) }
    ].map(tracker => {
      const { peaks, valleys } = findPeaksAndValleys(tracker.data);
      const trend = calculateTrend(tracker.data);
      const maxValue = Math.max(...tracker.data);
      const minValue = Math.min(...tracker.data);
      const maxIndex = tracker.data.indexOf(maxValue);
      const minIndex = tracker.data.indexOf(minValue);
      
      return {
        ...tracker,
        peaks,
        valleys,
        trend,
        maxValue,
        minValue,
        maxIndex,
        minIndex
      };
    });

    return (
      <div className="graph-section">
        <h3 className="graph-title">{title}</h3>
        <svg width={width} height={height} className="line-graph">
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
              <g key={i}>
                <line
                  x1={padding}
                  y1={padding + ratio * chartHeight}
                  x2={width - padding}
                  y2={padding + ratio * chartHeight}
                  stroke="#333"
                  strokeWidth="1"
                  opacity="0.3"
                />
                <text
                  x={padding - 10}
                  y={padding + ratio * chartHeight + 5}
                  fill="#888"
                  fontSize="12"
                  textAnchor="end"
                >
                  {formatValue(maxValue * (1 - ratio), type)}
                </text>
              </g>
            ))}
            
            {/* Time labels */}
            {historicalData.map((_, index) => {
              if (index % Math.ceil(historicalData.length / 6) === 0) {
                const date = new Date(historicalData[index].timestamp);
                return (
                  <text
                    key={index}
                    x={padding + (historicalData.length === 1 ? 0 : (index / (historicalData.length - 1))) * chartWidth}
                    y={height - 10}
                    fill="#888"
                    fontSize="10"
                    textAnchor="middle"
                  >
                    {date.toLocaleDateString()}
                  </text>
                );
              }
              return null;
            })}

            {/* Lines for each tracker */}
            {trackers.map((tracker, trackerIndex) => {
              const points = tracker.data.map((value, index) => ({
                x: padding + (tracker.data.length === 1 ? 0 : (index / (tracker.data.length - 1))) * chartWidth,
                y: padding + chartHeight - (maxValue > 0 ? (value / maxValue) * chartHeight : chartHeight)
              }));

              const pathData = points.map((point, index) => 
                `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
              ).join(' ');

              return (
                <g key={trackerIndex}>
                  <path
                    d={pathData}
                    fill="none"
                    stroke={tracker.color}
                    strokeWidth="2"
                    className="line-path"
                  />
                  {points.map((point, index) => {
                    const value = tracker.data[index];
                    const date = new Date(historicalData[index].timestamp);
                    const tooltipContent = `${tracker.name}\n${formatValue(value, type)}\n${date.toLocaleString()}`;
                    
                    // Determine if this point went up or down from the previous point
                    const isFirstPoint = index === 0;
                    const isUp = !isFirstPoint && value > tracker.data[index - 1];
                    const isDown = !isFirstPoint && value < tracker.data[index - 1];
                    
                    return (
                      <g key={index}>
                        {/* Data point circle */}
                        <circle
                          cx={point.x}
                          cy={point.y}
                          r="3"
                          fill={tracker.color}
                          className="data-point"
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setTooltip({
                              visible: true,
                              x: rect.left + rect.width / 2,
                              y: rect.top - 20,
                              content: tooltipContent
                            });
                          }}
                          onMouseLeave={() => {
                            setTooltip(prev => ({ ...prev, visible: false }));
                          }}
                          onMouseMove={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setTooltip(prev => ({
                              ...prev,
                              x: rect.left + rect.width / 2,
                              y: rect.top - 20
                            }));
                          }}
                        />
                        
                        {/* Up/Down arrow indicator */}
                        {!isFirstPoint && (
                          <text
                            x={point.x}
                            y={point.y - 8}
                            textAnchor="middle"
                            fontSize="10"
                            fill={isUp ? "#4ade80" : isDown ? "#f87171" : "#94a3b8"}
                            fontWeight="bold"
                          >
                            {isUp ? "↑" : isDown ? "↓" : "→"}
                          </text>
                        )}
                      </g>
                    );
                  })}
                </g>
              );
            })}

            {/* Peak and Low Markers - One per tracker */}
            {trackers.map((tracker, trackerIndex) => {
              const points = tracker.data.map((value, index) => ({
                x: padding + (tracker.data.length === 1 ? 0 : (index / (tracker.data.length - 1))) * chartWidth,
                y: padding + chartHeight - (maxValue > 0 ? (value / maxValue) * chartHeight : chartHeight)
              }));

              return (
                <g key={`markers-${trackerIndex}`}>
                  {/* Peak marker - only the first peak (most recent if multiple) */}
                  {tracker.peaks.length > 0 && (
                    <g key={`peak-${trackerIndex}`}>
                      <circle
                        cx={points[tracker.peaks[0]].x}
                        cy={points[tracker.peaks[0]].y - 12}
                        r="6"
                        fill="#ffd700"
                        stroke="#ffffff"
                        strokeWidth="2"
                        opacity="0.95"
                      />
                      <text
                        x={points[tracker.peaks[0]].x}
                        y={points[tracker.peaks[0]].y - 18}
                        textAnchor="middle"
                        fontSize="12"
                        fill="#ffffff"
                        fontWeight="bold"
                      >
                        ▲
                      </text>
                    </g>
                  )}

                  {/* Low marker - only the first valley (most recent if multiple) */}
                  {tracker.valleys.length > 0 && (
                    <g key={`low-${trackerIndex}`}>
                      <circle
                        cx={points[tracker.valleys[0]].x}
                        cy={points[tracker.valleys[0]].y + 12}
                        r="6"
                        fill="#ff6b6b"
                        stroke="#ffffff"
                        strokeWidth="2"
                        opacity="0.95"
                      />
                      <text
                        x={points[tracker.valleys[0]].x}
                        y={points[tracker.valleys[0]].y + 18}
                        textAnchor="middle"
                        fontSize="12"
                        fill="#ffffff"
                        fontWeight="bold"
                      >
                        ▼
                      </text>
                    </g>
                  )}
                </g>
              );
            })}
        </svg>
        
        {/* Enhanced Legend */}
        <div className="graph-legend">
          {trackers.map((tracker, index) => (
            <div key={index} className="legend-item">
              <div 
                className="legend-color" 
                style={{ backgroundColor: tracker.color }}
              />
              <div className="legend-details">
                <span className="legend-label">{tracker.name}</span>
                <div className="legend-stats">
                  <span className="legend-stat">
                    <span className="legend-icon peak-icon">▲</span> Peak: {formatValue(tracker.maxValue, type)}
                  </span>
                  <span className="legend-stat">
                    <span className="legend-icon low-icon">▼</span> Low: {formatValue(tracker.minValue, type)}
                  </span>
                  <span className="legend-stat">
                    {tracker.trend === 'rising' && <span className="trend-icon rising">↗</span>}
                    {tracker.trend === 'falling' && <span className="trend-icon falling">↘</span>}
                    {tracker.trend === 'stable' && <span className="trend-icon stable">→</span>}
                    {tracker.trend === 'rising' && ' Rising'}
                    {tracker.trend === 'falling' && ' Falling'}
                    {tracker.trend === 'stable' && ' Stable'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="history-container">
        <div className="loading">Loading historical data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="history-container">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="history-container">
      
      <div className="graphs-grid">
        {renderLineGraph('Total Torrents', 'totalTorrents', 'torrents')}
        {renderLineGraph('Weekly Active Users', 'activeThisWeek', 'users')}
        {renderLineGraph('Total Torrents Size', 'totalTorrentsSize', 'size')}
        {renderLineGraph('Seed Size', 'seedSize', 'size')}
        {renderLineGraph('Total Users', 'totalUsers', 'users')}
        {renderLineGraph('Total Peers', 'totalPeers', 'peers')}
      </div>
      
      {/* Tooltip */}
      {tooltip.visible && (
        <div 
          className="tooltip"
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translateX(-50%)'
          }}
        >
          {tooltip.content.split('\n').map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default History;
